import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Table,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Divider,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  Link,
  Badge,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { Loading } from "../../../components/Loading";
import { financeApi } from "../apiService";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import {
  smallButtonProp,
  tableHeaderStyles,
  tableRowStyles,
  commonTypographyProps,
  commonTextFieldProps,
  commonButtonProps,
} from "../../../utils/styles";
import {
  formatCurrency,
  formatDate,
  formatInputCurrency,
  formatText,
} from "../../../utils/utils";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { PROJECT_TERMINOLOGY } from "../../../project/project_module/enums";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../components/Toast";
import { getTransactionTypeColor } from "../Transactions";
import SearchableAutocomplete from "../../../components/SearchableAutocomplete";
import { SearchableEntity } from "../../../services/searchService";
import { PAYMENT_MODES } from "./PaymentModal";

interface CashBookPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onTransactionAdded: () => void;
}

const transactionTypes = {
  Paid: "Paid",
  Received: "Received",
};

const partyTypes = {
  SELECT_PARTY_TYPE: "SELECT_PARTY_TYPE",
  DRIVER: "DRIVER",
  CLEANER: "CLEANER",
  LADY: "LADY",
  PETROL_PUMP: "PETROL_PUMP",
  PARKING: "PARKING",
  MECHANIC: "MECHANIC",
  GARAGE: "GARAGE",
  SERVICE_OPERATOR: "SERVICE_OPERATOR",
  PICKUP_MAN: "PICKUP_MAN",
  CLIENT: "CLIENT",
  OUTSOURCED_OWNER: "OUTSOURCED_OWNER",
  OTHER: "OTHER",
};

// Initial form values
const initialValues = {
  partyType: partyTypes.SELECT_PARTY_TYPE,
  partyName: "",
  partyId: "",
  description: "",
  amount: "",
  transactionType: "Paid",
  date: new Date().toISOString().split("T")[0],
  paymentMode: "CASH",
};

// Validation schema
const validationSchema = Yup.object({
  partyType: Yup.string()
    .required("Party type is required")
    .notOneOf([partyTypes.SELECT_PARTY_TYPE], "Please select a party type"),
  partyName: Yup.string().when("partyType", {
    is: (partyType: string) => partyType !== partyTypes.OTHER,
    then: (schema) => schema.required("Party name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  partyId: Yup.string().when("partyType", {
    is: (partyType: string) => partyType !== partyTypes.OTHER,
    then: (schema) => schema.required("Please select a party"),
    otherwise: (schema) => schema.notRequired(),
  }),
  description: Yup.string().when("partyType", {
    is: (partyType: string) => partyType === partyTypes.OTHER,
    then: (schema) => schema.required("Description is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  amount: Yup.string()
    .required("Amount is required")
    .test("is-valid-amount", "Please enter a valid amount", (value) => {
      if (!value) return false;
      const numValue = parseFloat(value.replace(/,/g, ""));
      return !isNaN(numValue) && numValue > 0;
    }),
  transactionType: Yup.string().required("Transaction type is required"),
  date: Yup.string().required("Date is required"),
  paymentMode: Yup.string().required("Payment mode is required"),
});

const CashBookPaymentModal: React.FC<CashBookPaymentModalProps> = ({
  open,
  onClose,
  onTransactionAdded,
}) => {
  const { planStatus } = usePlanStatus();
  const { showToast } = useToast();

  const handleCreateTransaction = async (
    values: typeof initialValues,
    { resetForm }: any
  ) => {
    if (!planStatus?.company?.id) return;

    try {
      const transactionData = {
        ...values,
        partyId: values.partyId,
        partyType: values.partyType,
        paymentMode: values.paymentMode,
        context: "CASHBOOK",
        description: values.description,
        contextId: values.partyId,
        createdBy: planStatus?.user?.id,
        amount: parseFloat(values.amount.replace(/,/g, "")),
      };

      const response = await financeApi.createTransaction(transactionData);
      if (response) {
        // Reset form
        resetForm({ values: initialValues });

        // Notify parent component
        onTransactionAdded();

        showToast("Transaction created successfully", "success");
      } else {
        showToast();
      }
    } catch (error) {
      showToast();
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ minHeight: "500px" }}
      >
        <DialogTitle sx={{ px: 1, py: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" component="div">
              Add New Transaction
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: "grey.500" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleCreateTransaction}
            >
              {({ values, errors, touched, setFieldValue, isSubmitting }) => {
                const disabled =
                  !values.partyType ||
                  (values.partyType !== partyTypes.OTHER && !values.partyId);

                return (
                  <Form>
                    <Grid container spacing={1.5}>
                      <Grid container item xs={12} spacing={1.5}>
                        <Grid item xs={12} sm={3}>
                          <FormControl required fullWidth size="small">
                            <InputLabel sx={{ fontSize: "0.75rem" }}>
                              Party
                            </InputLabel>
                            <Field
                              as={Select}
                              name="partyType"
                              label="Party"
                              value={values.partyType}
                              onChange={(e: any) => {
                                setFieldValue("partyType", e.target.value);
                                setFieldValue("partyName", "");
                                setFieldValue("partyId", "");
                              }}
                              {...commonTextFieldProps.SelectProps}
                            >
                              {Object.entries(partyTypes).map(
                                ([key, value]) => (
                                  <MenuItem
                                    key={key}
                                    value={key}
                                    sx={{ fontSize: "0.75rem" }}
                                    disabled={
                                      key === partyTypes.SELECT_PARTY_TYPE
                                    }
                                  >
                                    {formatText(value)}
                                  </MenuItem>
                                )
                              )}
                            </Field>
                            {touched.partyType && errors.partyType && (
                              <Typography
                                color="error"
                                variant="caption"
                                sx={{
                                  fontSize: "0.65rem",
                                }}
                              >
                                {errors.partyType}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        {values.partyType !== partyTypes.OTHER && (
                          <Grid item xs={12} sm={6}>
                            <SearchableAutocomplete
                              entityType={values.partyType as SearchableEntity}
                              value={values.partyName}
                              onSelect={(selectedOption) => {
                                setFieldValue(
                                  "partyName",
                                  selectedOption?.name ||
                                    selectedOption?.petrolPumpName ||
                                    selectedOption?.partyName ||
                                    selectedOption?.clientName
                                );
                                setFieldValue("partyId", selectedOption?.id);
                              }}
                              onChange={(value) => {
                                setFieldValue("partyName", value);
                                setFieldValue("partyId", "");
                              }}
                              label="Party Name *"
                              placeholder="Search for party name"
                              getOptionLabel={(option) =>
                                typeof option === "string"
                                  ? option
                                  : option.name || ""
                              }
                              noOptionsText="No party found"
                              disabled={
                                values.partyType ===
                                partyTypes.SELECT_PARTY_TYPE
                              }
                              size="small"
                            />
                            {touched.partyName && errors.partyName && (
                              <Typography
                                color="error"
                                variant="caption"
                                sx={{
                                  fontSize: "0.65rem",
                                }}
                              >
                                {errors.partyName}
                              </Typography>
                            )}
                          </Grid>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormControl required fullWidth>
                          <InputLabel sx={{ fontSize: "0.75rem" }}>
                            Type
                          </InputLabel>
                          <Field
                            as={Select}
                            name="transactionType"
                            label="Type"
                            value={values.transactionType}
                            {...commonTextFieldProps.SelectProps}
                            disabled={disabled}
                          >
                            {Object.entries(transactionTypes).map(
                              ([key, value]) => (
                                <MenuItem
                                  key={key}
                                  value={value}
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {key}
                                </MenuItem>
                              )
                            )}
                          </Field>
                          {touched.transactionType &&
                            errors.transactionType && (
                              <Typography
                                color="error"
                                variant="caption"
                                sx={{ fontSize: "0.65rem" }}
                              >
                                {errors.transactionType}
                              </Typography>
                            )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormControl required fullWidth>
                          <InputLabel sx={{ fontSize: "0.75rem" }}>
                            Payment Mode
                          </InputLabel>
                          <Field
                            as={Select}
                            name="paymentMode"
                            label="Payment Mode"
                            value={values.paymentMode}
                            {...commonTextFieldProps.SelectProps}
                            disabled={disabled}
                          >
                            {Object.entries(PAYMENT_MODES).map(
                              ([key, value]) => (
                                <MenuItem
                                  key={key}
                                  value={key}
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {value}
                                </MenuItem>
                              )
                            )}
                          </Field>
                          {touched.paymentMode && errors.paymentMode && (
                            <Typography
                              color="error"
                              variant="caption"
                              sx={{ fontSize: "0.65rem" }}
                            >
                              {errors.paymentMode}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          name="date"
                          label="Date"
                          type="date"
                          required
                          fullWidth
                          value={values.date}
                          InputLabelProps={{ shrink: true }}
                          {...commonTextFieldProps}
                          disabled={disabled}
                        />
                        {touched.date && errors.date && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            {errors.date}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Field
                          as={TextField}
                          name="amount"
                          label="Amount"
                          type="text"
                          required
                          fullWidth
                          value={
                            values.amount
                              ? formatInputCurrency(values.amount)
                              : ""
                          }
                          onChange={(e: any) => {
                            const value = e.target.value.replace(/,/g, "");
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              setFieldValue("amount", value);
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <span
                                style={{ marginRight: 8, fontSize: "0.75rem" }}
                              >
                                ₹
                              </span>
                            ),
                            inputProps: {
                              style: { textAlign: "left" },
                              maxLength: 12,
                              inputMode: "decimal",
                            },
                          }}
                          {...commonTextFieldProps}
                          disabled={disabled}
                        />
                        {touched.amount && errors.amount && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            {errors.amount}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Field
                          as={TextField}
                          name="description"
                          label="Reason"
                          required={values.partyType === partyTypes.OTHER}
                          fullWidth
                          value={values.description}
                          {...commonTextFieldProps}
                          disabled={disabled}
                        />
                        {touched.description && errors.description && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            {errors.description}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={handleClose}
                            color="error"
                            {...commonButtonProps}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            {...commonButtonProps}
                          >
                            {isSubmitting ? "Adding..." : "Add Payment"}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CashBookPaymentModal;
